import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";

export interface ActivityType {
  id: string;
  organization_id: string;
  slug: string;
  name: string;
  name_en: string | null;
  description: string | null;
  description_en: string | null;
  icon: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  player_can_create: boolean;
  result_reporting: boolean;
  default_spots: number;
  default_max_participants: number | null;
  min_participants: number | null;
  default_duration_minutes: number;
  default_price_per_person: number;
  // Sprint D PR-AB Phase 4 dual-state cols (öre + currency).
  default_price_per_person_minor?: number | null;
  price_per_person_minor?: number | null;
  member_price_per_person_minor?: number | null;
  currency?: string;
  default_min_level: number | null;
  default_max_level: number | null;
  default_gender_filter: string | null;
  default_approval_mode: string | null;
  default_payment_mode: string | null;
  requires_courts: boolean;
  min_courts: number | null;
  max_courts: number | null;
  is_system_default: boolean;
  tournament_format: string | null;
  default_points_per_match: number;
  title_placeholder: string | null;
  updated_at: string | null;
}

const EMPTY: ActivityType[] = [];

export function useActivityTypes() {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["activity-types", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activity_types" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as ActivityType[];
    },
    enabled: !!organization?.id,
  });

  const allQuery = useQuery({
    queryKey: ["activity-types-all", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activity_types" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as ActivityType[];
    },
    enabled: !!organization?.id,
  });

  const upsert = useMutation({
    mutationFn: async (type: Partial<ActivityType> & { organization_id: string }) => {
      if (type.id) {
        const { error } = await supabase.from("activity_types" as any).update(type as any).eq("id", type.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("activity_types" as any).insert(type as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activity-types"] });
      queryClient.invalidateQueries({ queryKey: ["activity-types-all"] });
      toast.success("Aktivitetstyp sparad");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("activity_types" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activity-types"] });
      queryClient.invalidateQueries({ queryKey: ["activity-types-all"] });
      toast.success("Aktivitetstyp borttagen");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return {
    types: query.data ?? EMPTY,
    allTypes: allQuery.data ?? EMPTY,
    isLoading: query.isLoading,
    upsert,
    remove,
  };
}
